import { HttpClient, httpResource } from '@angular/common/http';
import type { Signal } from '@angular/core';
import { inject, Injectable } from '@angular/core';

import type {
  ADARampAssetResponse,
  UpdateADARampAssetRequest,
} from '../../domain/assets/ada-ramp-asset.model';
import type { AssetCountByConditionAndMaterialResponse } from '../../domain/assets/asset-visualization.model';
import { assertIsDefined } from '../../domain/types';
import type { ChartData } from '../../domain/ui/chart-data.model';
import type { GaugeData } from '../../domain/ui/radial-gauge.model';
import { DataInvalidationService } from '../data-invalidation.service';
import { TenantContext } from '../tenant.context';

@Injectable({
  providedIn: 'root',
})
export class ADARampAssetService {
  readonly #httpClient = inject(HttpClient);
  readonly #tenantContext = inject(TenantContext);
  readonly #dataInvalidationService = inject(DataInvalidationService);

  adaRampAssetResource(adaRampAssetId: Signal<string>) {
    return httpResource<ADARampAssetResponse>(() => {
      const tenantId = this.#tenantContext.currentTenantId();
      const adaRampAssetIdValue = adaRampAssetId();
      return tenantId && adaRampAssetIdValue
        ? `api/tenants/${tenantId}/assets/ada-ramps/${adaRampAssetIdValue}`
        : undefined;
    });
  }

  updateADARampAsset(assetId: string, request: UpdateADARampAssetRequest) {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);
    return this.#httpClient.put<ADARampAssetResponse>(
      `api/tenants/${tenantId}/assets/ada-ramps/${assetId}`,
      request,
    );
  }

  adaRampAssetAverageConditionScoreResource() {
    return httpResource<GaugeData>(() => {
      this.#dataInvalidationService.track('assets');
      const tenantId = this.#tenantContext.currentTenantId();
      return tenantId
        ? `api/tenants/${tenantId}/assets/ada-ramps/visualization/average-condition-score`
        : undefined;
    });
  }

  adaRampAssetCountByConditionResource() {
    return httpResource<ChartData[]>(() => {
      this.#dataInvalidationService.track('assets');
      const tenantId = this.#tenantContext.currentTenantId();
      return tenantId
        ? `api/tenants/${tenantId}/assets/ada-ramps/visualization/count-by-condition`
        : undefined;
    });
  }

  adaRampAssetCountByConditionAndMaterialResource() {
    return httpResource<AssetCountByConditionAndMaterialResponse[]>(() => {
      this.#dataInvalidationService.track('assets');
      const tenantId = this.#tenantContext.currentTenantId();
      return tenantId
        ? `api/tenants/${tenantId}/assets/ada-ramps/visualization/asset-count-by-condition-and-material`
        : undefined;
    });
  }
}
